/**
 * `useTokenMetadata` — decimals and a ticker for a set of tokens, batched.
 *
 * The read `ToGraphViewOptions.prices` has always wanted and nothing ever
 * supplied. `edgeView` resolves an edge's `amountDisplay`, `symbol` and
 * `valueUsd` through that map, so with it empty every graph edge in the product
 * drew as an unlabelled line: `amountDisplay` undefined because `decimals` was
 * undefined, and no ticker at all. On surfaces whose subject is "follow the
 * money", the amount was the one thing not drawn.
 *
 * It answers TWO shapes, and they are not interchangeable:
 *
 * - {@link TokenMetadataResult.prices} — the flat, address-keyed map the
 *   transposers read. `tokenPriceFor` keys by the address alone, so a token two
 *   chains disagree about is published by NEITHER (see `tokenPricesFromIndex`).
 * - {@link TokenMetadataResult.index} — the chain-qualified lookup, for a
 *   caller that has the chain in hand and wants the exact answer. The hop
 *   ladder uses this one, because it keys its assets by chain already.
 *
 * Not a price read. `price_usd` is always null on these rows — price lives on
 * `AssetTicker`, keyed `(coin_id, source)`, with no batch route and a snapshot
 * its own service says is not the live price. So an edge gets its amount and
 * its ticker and `valueUsd` stays absent, which is the honest answer rather
 * than a number nobody would stand behind.
 */
import * as React from "react";
import { useQueries } from "@tanstack/react-query";
import { useApiClient } from "@authe/ui-core/api";
import { useLoadBudget } from "@authe/ui-core/quota";
import { usePermissions } from "@authe/ui-core/permissions";
import {
  boundTokenRefs,
  buildTokenMetadataIndex,
  graphTokenRefs,
  tokenPricesFromIndex,
  type TokenMetadata,
  type TokenMetadataIndex,
  type TokenMetadataRef,
} from "@authe/fe-commons/tokens";
import { normalizeAddress } from "@authe/fe-commons/chains";
import type { TokenPriceDTO } from "@authe/fe-commons/models";
import { RESOURCES } from "@/config";
import {
  TOKEN_METADATA_STALE_MS,
  enqueueTokenMetadataRef,
  tokenMetadataQueryKey,
} from "./tokenMetadataRefCache";

export interface UseTokenMetadataOptions {
  /** Caller gate on top of the permission check. Default true. */
  enabled?: boolean;
  /** Override the plan budget. Leave alone unless the surface is special. */
  maxRefs?: number;
}

export interface TokenMetadataResult {
  /** Address-keyed, for `toGraph` / `toGraphView` / `toTokenFlowRows`. */
  prices: Record<string, TokenPriceDTO>;
  /** Chain-qualified, for a caller that knows which chain it is asking about. */
  index: TokenMetadataIndex;
  /** Symbol resolver matching `ToGraphViewOptions.resolveSymbol`. */
  resolveSymbol: (tokenAddress: string | null | undefined) => string | undefined;
  /** Distinct tokens the caller named, before the budget was applied. */
  requestedCount: number;
  /** How many were actually asked about. */
  queriedCount: number;
  /** True when the budget cut the list short — the surface can say so. */
  truncated: boolean;
  /**
   * Addresses two chains answered differently for, and which therefore carry
   * no entry in {@link prices}. They are still in {@link index}, per chain.
   */
  ambiguous: string[];
  /** False when the caller may not read token definitions, or asked not to. */
  allowed: boolean;
  isLoading: boolean;
}

const NO_SYMBOL = (): undefined => undefined;

/** Definitions for an explicit set of `(chain, token)` refs. */
export function useTokenMetadata(
  refs: readonly TokenMetadataRef[],
  options: UseTokenMetadataOptions = {},
): TokenMetadataResult {
  const { enabled = true } = options;
  const client = useApiClient();
  const budget = useLoadBudget("tokens.metadata");
  const { can } = usePermissions();

  // Checked here rather than letting the request 403, because a denial on this
  // read is invisible: the graph draws exactly as it did before, with no labels
  // and no reason given. `/tokens/batch` carries `scope:tokens:read`, which all
  // three built-in tiers hold — it used to sit on the collection's `ANY` mount,
  // where the `tokens:edit` tag denied it to every Reader in the product.
  const allowed = enabled && can(RESOURCES.tokens, "read");

  const bounded = React.useMemo(
    () => boundTokenRefs(refs, options.maxRefs ?? budget.enrichment),
    [refs, options.maxRefs, budget.enrichment],
  );

  const results = useQueries({
    queries: bounded.refs.map((ref) => ({
      queryKey: tokenMetadataQueryKey(ref),
      enabled: allowed,
      staleTime: TOKEN_METADATA_STALE_MS,
      queryFn: () => enqueueTokenMetadataRef(ref, client),
    })),
  });

  // `useQueries` hands back a fresh array every render, so the fold is keyed on
  // what actually changed: which tokens have answered, and when each last did.
  const stamp = results.map((r) => r.dataUpdatedAt).join(",");
  const rows: TokenMetadata[] = React.useMemo(
    () => results.flatMap((r) => (r.data?.metadata ? [r.data.metadata] : [])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stamp],
  );

  const index = React.useMemo(() => buildTokenMetadataIndex(rows), [rows]);
  const flattened = React.useMemo(() => tokenPricesFromIndex(index), [index]);

  // Chain-less, because that is the seam's shape. It wins over the price's own
  // symbol in `edgeView`, so it must not answer where the flat map elided an
  // ambiguous token — saying "USDC" on an amount rendered at an unknown scale
  // dresses an unreadable number up as a known one.
  //
  // Folded through `normalizeAddress`, not `toLowerCase`. With no chain to
  // dispatch on it folds by SHAPE, which is what `tokenPriceFor` does on the
  // other side of this map: an EVM address lower-cases and a Solana mint keeps
  // its case, because the lower-cased spelling of a mint names a different,
  // non-existent account and would simply never match.
  const resolveSymbol = React.useMemo(() => {
    if (index.size === 0) return NO_SYMBOL;
    return (tokenAddress: string | null | undefined): string | undefined => {
      if (!tokenAddress) return undefined;
      return flattened.prices[normalizeAddress(tokenAddress)]?.symbol ?? undefined;
    };
  }, [index, flattened]);

  return {
    prices: flattened.prices,
    index,
    resolveSymbol,
    ambiguous: flattened.ambiguous,
    requestedCount: bounded.requestedCount,
    queriedCount: bounded.refs.length,
    truncated: bounded.truncated,
    allowed,
    isLoading: allowed && results.some((r) => r.isLoading),
  };
}

/**
 * The same read, driven off a graph's own edges.
 *
 * Takes the graph untyped for the reason the hop ladder does: the sources hold
 * a stored `InteractionGraph` whose twenty-field shape nobody wants to
 * hand-copy, and a merged live-feed graph is the same rows again.
 */
export function useGraphTokenMetadata(
  graph: unknown,
  options: UseTokenMetadataOptions = {},
): TokenMetadataResult {
  const refs = React.useMemo(() => graphTokenRefs(graph), [graph]);
  return useTokenMetadata(refs, options);
}
